import { ErrorType } from '@/types';

export { ErrorType };

export interface AppError extends Error {
  type: ErrorType;
  statusCode?: number;
  context?: string;
}

export class ApiError extends Error implements AppError {
  type: ErrorType;
  statusCode?: number;
  context?: string;

  constructor(
    message: string,
    type: ErrorType = ErrorType.UNKNOWN_ERROR,
    statusCode?: number,
    context?: string
  ) {
    super(message);
    this.name = 'ApiError';
    this.type = type;
    this.statusCode = statusCode;
    this.context = context;
  }
}

export function createErrorFromResponse(error: any, context?: string): AppError {
  if (error.response) {
    const status = error.response.status;
    const message = error.response.data?.message || error.message || 'An error occurred';
    
    let type: ErrorType;
    switch (status) {
      case 401:
      case 403:
        type = ErrorType.AUTHENTICATION_ERROR;
        break;
      case 404:
        type = ErrorType.SESSION_NOT_FOUND;
        break;
      case 400:
        type = ErrorType.VALIDATION_ERROR;
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        type = ErrorType.AGENT_ERROR;
        break;
      default:
        type = ErrorType.UNKNOWN_ERROR;
    }
    
    return new ApiError(message, type, status, context);
  }
  
  if (error.request) {
    return new ApiError(
      'Network error - please check your connection',
      ErrorType.NETWORK_ERROR,
      undefined,
      context
    );
  }
  
  return new ApiError(
    error.message || 'An unexpected error occurred',
    ErrorType.UNKNOWN_ERROR,
    undefined,
    context
  );
}

export function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  return 'An unexpected error occurred';
}

export function isNetworkError(error: unknown): boolean {
  return error instanceof ApiError && error.type === ErrorType.NETWORK_ERROR;
}

export function isAuthenticationError(error: unknown): boolean {
  return error instanceof ApiError && error.type === ErrorType.AUTHENTICATION_ERROR;
}

export async function retryOperation<T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> {
  let lastError: unknown;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      // Don't retry on authentication or validation errors
      if (isAuthenticationError(error) || 
          (error instanceof ApiError && error.type === ErrorType.VALIDATION_ERROR)) {
        throw error;
      }
      
      // If this was the last attempt, throw the error
      if (attempt === maxRetries) {
        throw error;
      }
      
      // Wait before retrying (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, attempt)));
    }
  }
  
  throw lastError;
}